/**
 * AG-UI message conversion + /connect fetch.
 *
 * Converts the backend's flat AG-UI message stream (alternating
 * user/assistant/tool entries) into the parts-based ThreadMessageLike
 * shape assistant-ui expects.
 */
import type { ThreadMessageLike } from "@assistant-ui/react";
import type { AssistantMessageCustomMetadata, TurnFeedback } from "~/components/assistant/types";
import { ConversationStatus } from "~/components/assistant/types";
import { apiClient, isApiError } from "~/utils/apiClient";

interface AguiToolCall {
  id: string;
  type: string;
  function: { name: string; arguments: string };
}

export interface AguiMessage {
  role: "user" | "assistant" | "tool";
  content: string | null;
  id?: string;
  toolCalls?: AguiToolCall[];
  toolCallId?: string;
  error?: string | null;
  cancelled?: boolean;
  turnId?: string;
  turnFeedback?: TurnFeedback | null;
}

type ContentPart =
  | { readonly type: "text"; readonly text: string }
  | {
      readonly type: "tool-call";
      readonly toolCallId: string;
      readonly toolName: string;
      readonly args: Record<string, unknown>;
      readonly argsText: string;
      result?: string | null;
      isError?: boolean;
    };

/**
 * Merges consecutive assistant + tool messages into a single assistant
 * message per user turn, matching the structure assistant-ui expects.
 */
export function convertAguiToThreadMessages(aguiMessages: AguiMessage[]): ThreadMessageLike[] {
  const result: ThreadMessageLike[] = [];
  let currentParts: ContentPart[] = [];
  let lastTurnId: string | undefined;
  let lastTurnFeedback: TurnFeedback | null | undefined;
  let pendingCancelled = false;

  const flushAssistant = () => {
    if (currentParts.length > 0 || pendingCancelled) {
      const custom: AssistantMessageCustomMetadata = {};
      if (lastTurnId) custom.turnId = lastTurnId;
      if (lastTurnFeedback) custom.feedback = lastTurnFeedback;
      if (pendingCancelled) custom.cancelled = true;

      // Mirror RunAggregator's RUN_FINISHED behavior: when a hydrated assistant
      // message has unresolved tool calls, mark it requires-action so the AG-UI
      // runtime's addToolResult will trigger a follow-up turn after the user
      // submits the rehydrated tool. Without this, status defaults to "complete"
      // and addToolResult silently stores the result without resuming the run.
      const hasPendingToolCall =
        !pendingCancelled &&
        currentParts.some((p) => p.type === "tool-call" && p.result === undefined);

      result.push({
        role: "assistant",
        content: currentParts as ThreadMessageLike["content"],
        ...(hasPendingToolCall && {
          status: { type: "requires-action", reason: "tool-calls" } as const,
        }),
        metadata: Object.keys(custom).length > 0 ? { custom } : undefined,
      });
      currentParts = [];
      pendingCancelled = false;
    }
  };

  for (const msg of aguiMessages) {
    if (msg.role === "user") {
      flushAssistant();
      lastTurnId = msg.turnId;
      lastTurnFeedback = msg.turnFeedback;
      // Preserve `msg.id` so the in-flight prompt has a stable id the
      // replay attach can target as `resumeRun`'s parentId — without it,
      // assistant-ui assigns a fresh id and `resetHead(null)` wipes the
      // message we just seeded.
      result.push({
        ...(msg.id ? { id: msg.id } : {}),
        role: "user",
        content: msg.content ?? "",
        metadata: msg.turnId ? { custom: { turnId: msg.turnId } } : undefined,
      });
    } else if (msg.role === "assistant") {
      if (msg.cancelled) {
        pendingCancelled = true;
        continue;
      }
      if (msg.toolCalls) {
        for (const tc of msg.toolCalls) {
          let args: Record<string, unknown> = {};
          try {
            args = JSON.parse(tc.function.arguments);
          } catch {
            // keep empty args if parse fails
          }
          currentParts.push({
            type: "tool-call",
            toolCallId: tc.id,
            toolName: tc.function.name,
            args,
            argsText: tc.function.arguments,
          });
        }
      }
      if (msg.content && !msg.toolCalls) {
        currentParts.push({ type: "text", text: msg.content });
      }
    } else if (msg.role === "tool") {
      for (let i = currentParts.length - 1; i >= 0; i--) {
        const part = currentParts[i];
        if (part.type === "tool-call" && part.toolCallId === msg.toolCallId) {
          part.result = msg.content;
          if (msg.error) part.isError = true;
          break;
        }
      }
    }
  }

  flushAssistant();
  return result;
}

export interface ConnectPayload<S = unknown> {
  state: S;
  status: ConversationStatus;
  messages: ThreadMessageLike[];
}

/**
 * Fetches `/conversations/{id}/connect` and converts the AG-UI message
 * stream into ThreadMessageLike[]. Returns `null` if the backend returns
 * 400 (e.g. a freshly-created conversation with no committed history);
 * other errors propagate so the caller can decide how to surface them.
 */
export async function fetchConnect<S = unknown>(
  baseUrl: string,
  conversationId: string
): Promise<ConnectPayload<S> | null> {
  try {
    const resp = await apiClient<{
      state: S;
      status: ConversationStatus;
      messages: AguiMessage[];
    }>(`${baseUrl}/conversations/${conversationId}/connect`);
    return {
      state: resp.state,
      status: resp.status,
      messages: resp.messages.length ? convertAguiToThreadMessages(resp.messages) : [],
    };
  } catch (error) {
    if (isApiError(error) && error.status === 400) {
      return null;
    }
    throw error;
  }
}
